import type { CSSProperties } from "react";
import type { NavbarFragmentResult } from "@/lib/sanity/sanity.types";
import { brandColorHex } from "@/lib/color-utils";

/**
 * Design color value derived from the CMS navbar fragment.
 * Stays in sync with Sanity-generated types automatically.
 */
type NavigationData = NonNullable<NavbarFragmentResult>;
type PrimaryMenuItem = NonNullable<NavigationData["primaryMenuItems"]>[number];
type RichMenuItem = Extract<PrimaryMenuItem, { type: "richMenu" }>;
export type DesignColorValue = NonNullable<RichMenuItem["colour"]>;

/**
 * Get the hex value for a design color token.
 */
export function getDesignColorHex(
  colour: DesignColorValue | null | undefined,
): string | undefined {
  if (!colour) return undefined;
  return brandColorHex[colour];
}

/**
 * Get inline style for icon circle tint (~10% opacity background).
 * Returns undefined when colour is null so the fallback class applies.
 */
export function getIconTintStyle(
  colour: DesignColorValue | null | undefined,
): CSSProperties | undefined {
  const hex = getDesignColorHex(colour);
  if (!hex) return undefined;
  return { backgroundColor: `${hex}1a` };
}

/**
 * Get inline style for the icon glyph — full-strength theme colour so
 * the icon matches the menu's accent tone (rather than default navy).
 */
export function getIconColorStyle(
  colour: DesignColorValue | null | undefined,
): CSSProperties | undefined {
  const hex = getDesignColorHex(colour);
  if (!hex) return undefined;
  return { color: hex };
}
